import axios from 'axios';

// Set the API base URL
// Use the full backend URL directly
const ensureProtocol = (url) => {
  if (!url) {
    return url;
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  if (typeof window !== 'undefined' && window.location?.protocol) {
    return `${window.location.protocol}//${url.replace(/^\/\//, '')}`;
  }

  return `https://${url.replace(/^\/\//, '')}`;
};

const appendApiPath = (url) => {
  if (!url) {
    return url;
  }

  const trimmed = url.replace(/\/+$/, '');

  if (trimmed.toLowerCase().endsWith('/api')) {
    return trimmed;
  }

  return `${trimmed}/api`;
};

// In development, use production endpoint
// In production, use the production base URL
const getApiBaseUrl = () => {
  // If explicitly set via environment variable, use it
  if (process.env.REACT_APP_API_BASE_URL) {
    const envUrl = process.env.REACT_APP_API_BASE_URL.trim();
    // If it's just '/api' or empty, use production URL
    if (envUrl === '/api' || envUrl === '') {
     return "/api";
    //  return 'http://localhost:6062/api';
    }
    return appendApiPath(ensureProtocol(envUrl));
  }

  // Default base URL - production URL
 return "/api";
//  return 'http://localhost:6062/api';
};

const API_BASE_URL = getApiBaseUrl();

if (typeof console !== 'undefined' && process.env.NODE_ENV !== 'production') {
  console.info('[API] Base URL configured as:', API_BASE_URL);
}

const AUDIT_IDENTIFIER_KEYS = [
  'name',
  'stateName',
  'districtName',
  'talukaName',
  'professionName',
  'religionName',
  'categoryName',
  'castName',
  'constituencyName',
  'yojnaName',
  'schemeName',
  'groupName',
  'userGroupName',
  'username',
  'firstName',
  'title'
];

const toReadableLabel = (key) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const getStoredUserIdFallback = () => {
  const storedId = localStorage.getItem('userId');
  if (!storedId) {
    return null;
  }

  const numeric = Number(storedId);
  return Number.isNaN(numeric) ? storedId : numeric;
};

const getCurrentUserId = () => {
  const fallbackUserId = getStoredUserIdFallback();
  const rawUser = localStorage.getItem('user');
  if (!rawUser) {
    return fallbackUserId;
  }

  try {
    const parsed = JSON.parse(rawUser);
    return (
      parsed?.userId ??
      parsed?.id ??
      parsed?.user?.userId ??
      parsed?.user?.id ??
      parsed?.appUserId ??
      fallbackUserId
    );
  } catch (error) {
    console.error('Failed to parse stored user info for audit logging:', error);
    return fallbackUserId;
  }
};

const getCurrentUserName = () => {
  const cachedName = localStorage.getItem('userName');
  if (cachedName && cachedName.trim()) {
    return cachedName.trim();
  }

  const rawUser = localStorage.getItem('user');
  if (!rawUser) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawUser);
    const firstName =
      parsed?.firstName ??
      parsed?.user?.firstName ??
      parsed?.userDetails?.firstName ??
      '';
    const lastName =
      parsed?.lastName ??
      parsed?.user?.lastName ??
      parsed?.userDetails?.lastName ??
      '';
    const username =
      parsed?.username ??
      parsed?.user?.username ??
      parsed?.userDetails?.username ??
      '';
    const fallback =
      parsed?.employeeCode ??
      parsed?.user?.employeeCode ??
      parsed?.userDetails?.employeeCode ??
      '';

    const fullName = `${firstName} ${lastName}`.trim();
    const derived = fullName || username || fallback;
    if (derived) {
      localStorage.setItem('userName', derived);
      return derived;
    }
  } catch (error) {
    console.error('Failed to derive user name for audit logging:', error);
  }

  return null;
};

const getIdentifierValue = (identifier, newData = {}, previousData = {}) => {
  if (identifier) {
    return identifier;
  }

  const sourceCandidates = [newData, previousData];

  for (const source of sourceCandidates) {
    if (!source || typeof source !== 'object') continue;

    for (const key of AUDIT_IDENTIFIER_KEYS) {
      if (source[key]) {
        return source[key];
      }
    }

    if (source?.userGroup?.groupName) {
      return source.userGroup.groupName;
    }

    if (source?.state?.stateName) {
      return source.state.stateName;
    }
  }

  return null;
};

const describeChanges = (previousData = {}, newData = {}) => {
  if (!previousData || typeof previousData !== 'object') {
    return [];
  }

  const changes = [];

  Object.keys(newData || {}).forEach((key) => {
    if (key === 'auditData') return;

    const oldValue = previousData[key];
    const newValue = newData[key];

    // Special handling for dynamicFields array
    if (key === 'dynamicFields' && Array.isArray(newValue)) {
      const oldFields = Array.isArray(oldValue) ? oldValue : [];
      const newFields = newValue;

      // Create maps for easier lookup by fieldKey or id
      const oldFieldMap = new Map();
      oldFields.forEach((field) => {
        const identifier = field?.fieldKey || field?.fieldId || field?.id;
        if (identifier) {
          oldFieldMap.set(identifier, field);
        }
      });

      const newFieldMap = new Map();
      newFields.forEach((field) => {
        const identifier = field?.fieldKey || field?.fieldId || field?.id;
        if (identifier) {
          newFieldMap.set(identifier, field);
        }
      });

      // Find added fields (in new but not in old)
      newFields.forEach((field) => {
        const identifier = field?.fieldKey || field?.fieldId || field?.id;
        const fieldLabel = field?.fieldLabel || identifier || 'Unknown';
        if (!identifier || !oldFieldMap.has(identifier)) {
          changes.push(`${fieldLabel} is added by user`);
        }
      });

      // Find updated fields (in both but changed)
      newFields.forEach((field) => {
        const identifier = field?.fieldKey || field?.fieldId || field?.id;
        const oldField = identifier ? oldFieldMap.get(identifier) : null;
        if (oldField) {
          const fieldLabel = field?.fieldLabel || identifier || 'Unknown';
          const hasChanged =
            field?.fieldLabel !== oldField?.fieldLabel ||
            field?.fieldType !== oldField?.fieldType ||
            field?.isRequired !== oldField?.isRequired ||
            field?.isYojanaFilter !== oldField?.isYojanaFilter ||
            field?.filterOperator !== oldField?.filterOperator;
          if (hasChanged) {
            changes.push(`${fieldLabel} is updated by user`);
          }
        }
      });

      // Find deleted fields (in old but not in new)
      oldFields.forEach((field) => {
        const identifier = field?.fieldKey || field?.fieldId || field?.id;
        const fieldLabel = field?.fieldLabel || identifier || 'Unknown';
        if (!identifier || !newFieldMap.has(identifier)) {
          changes.push(`${fieldLabel} is deleted by user`);
        }
      });

      return;
    }

    if (key === 'filters' && typeof newValue === 'object' && newValue !== null) {
      const oldObj = typeof oldValue === 'object' && oldValue !== null ? oldValue : {};
      if (JSON.stringify(oldObj) !== JSON.stringify(newValue)) {
        changes.push('Filters is updated by user');
      }
      return;
    }

    if (key === 'submissionData' && Array.isArray(newValue)) {
      const oldArr = Array.isArray(oldValue) ? oldValue : [];
      if (JSON.stringify(oldArr) !== JSON.stringify(newValue)) {
        changes.push('Filters is updated by user');
      }
      return;
    }

    if (typeof newValue === 'object' && newValue !== null && !Array.isArray(newValue)) {
      const nestedChanges = describeChanges(oldValue || {}, newValue || {});
      const nestedLabel = toReadableLabel(key);
      if (nestedChanges.length > 0) {
        changes.push(`${nestedLabel} is updated by user`);
      }
    } else if (typeof newValue !== 'object' || newValue === null) {
      if (oldValue === undefined || oldValue === null) {
        if (newValue !== undefined && newValue !== null) {
          const label = toReadableLabel(key);
          changes.push(`${label} is updated by user`);
        }
        return;
      }
      if (oldValue !== newValue) {
        const label = toReadableLabel(key);
        changes.push(`${label} is updated by user`);
      }
    }
  });

  return changes;
};

const buildAuditMessage = ({
  action,
  resource,
  identifier,
  previousData,
  newData,
  message
}) => {
  if (message) {
    return message;
  }

  const identifierValue = getIdentifierValue(
    identifier,
    newData,
    previousData
  );

  const identifierText = identifierValue
    ? ` ${identifierValue}`
    : '';

  const yojanaIdPart =
    resource === 'Scheme'
      ? (previousData?.yojnaId ?? newData?.yojnaId ?? null)
      : null;
  const prefix = yojanaIdPart != null ? `yojanaId: ${yojanaIdPart}. ` : '';

  switch (action) {
    case 'create':
      return `${prefix}New ${resource}${identifierText} added.`;
    case 'delete':
      return `${prefix}${resource}${identifierText} deleted.`;
    case 'update': {
      const changes = describeChanges(previousData, newData);
      if (changes.length > 0) {
        return `${prefix}${changes.join('. ')}`;
      }
      return `${prefix}${resource}${identifierText} updated.`;
    }
    default:
      return `${prefix}${resource}${identifierText} ${action}.`;
  }
};

const appendAuditData = (
  payload = {},
  {
    action,
    resource,
    identifier,
    previousData,
    message,
    newData
  }
) => {
  const userId = getCurrentUserId();
  const userName = getCurrentUserName();

  const composedMessage = buildAuditMessage({
    action,
    resource,
    identifier,
    previousData,
    newData: newData || payload,
    message
  });

  return {
    ...payload,
    auditData: {
      userId,
      userName,
      message: composedMessage
    }
  };
};

const buildAuditOnlyPayload = (auditMeta) => ({
  auditData: appendAuditData({}, auditMeta).auditData
});

/** Returns an array of human-readable change strings between previous and new data (for display in approval UIs). */
export const describeChangesForDisplay = (previousData = {}, newData = {}) =>
  describeChanges(previousData, newData);

// Create axios instance with updated config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Only use withCredentials in production when making direct requests
  // In development, proxy handles CORS
  withCredentials: process.env.NODE_ENV === 'production',
  timeout: 20000
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    // Choose token by active module to avoid sending wrong auth-token.
    const adminToken = localStorage.getItem('token');
    const adToken = localStorage.getItem('adToken');
    const pathname = typeof window !== 'undefined' ? window.location?.pathname || '' : '';
    const isAdsModuleRoute = pathname.startsWith('/ads');
    const token = isAdsModuleRoute ? (adToken || adminToken) : (adminToken || adToken);

    if (token) {
      config.headers['auth-token'] = token;
    }
    // Log request in development
    if (process.env.NODE_ENV !== 'production') {
      console.log('[API] Making request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        fullURL: `${config.baseURL}${config.url}`
      });
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Enhanced error logging
    if (error.config) {
      console.error('[API] Request failed:', {
        method: error.config.method?.toUpperCase(),
        url: error.config.url,
        baseURL: error.config.baseURL,
        fullURL: `${error.config.baseURL}${error.config.url}`,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
    }
    
    if (error.response) {
      // Handle specific error cases
      switch (error.response.status) {
        case 401:
          {
            // Handle unauthorized access for both admin and ads modules.
            const pathname = typeof window !== 'undefined' ? window.location?.pathname || '' : '';
            const isAdsModuleRoute = pathname.startsWith('/ads');
            if (isAdsModuleRoute) {
              localStorage.removeItem('adToken');
              localStorage.removeItem('adUser');
              localStorage.removeItem('adUserId');
              if (!pathname.includes('/ads/login')) {
                window.location.href = '/ads/login';
              }
            } else {
              localStorage.removeItem('token');
              if (!pathname.includes('/login')) {
                window.location.href = '/login';
              }
            }
          }
          break;
        case 403:
          // Handle forbidden access
          console.error('Access forbidden');
          break;
        case 404:
          // Handle not found
          console.error('Resource not found');
          break;
        case 500:
          // Handle server error
          console.error('Server error');
          break;
        default:
          console.error('An error occurred');
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('[API] No response received:', {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        message: error.message
      });
    }
    return Promise.reject(error);
  }
);

// Masters API calls - States
export const fetchStates = () => api.get('/state');
export const addState = (stateData, auditContext = {}) =>
  api.post(
    '/state',
    appendAuditData(stateData, {
      action: 'create',
      resource: 'State',
      ...auditContext
    })
  );
export const updateState = (stateId, stateData, auditContext = {}) =>
  api.put(
    `/state/${stateId}`,
    appendAuditData(stateData, {
      action: 'update',
      resource: 'State',
      ...auditContext
    })
  );
export const deleteState = (stateId, auditContext = {}) =>
  api.delete(`/state/${stateId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'State',
      ...auditContext
    })
  });

// Masters API calls - Districts
export const fetchDistricts = () => api.get('/district');
export const fetchDistrictsByState = (stateId) => {
  console.log("[API] Fetching districts for state ID:", stateId);
  console.log("[API] State ID type:", typeof stateId);
  
  if (!stateId) {
    console.error("[API] State ID is missing or invalid:", stateId);
    return Promise.reject(new Error('State ID is required'));
  }
  
  // Ensure stateId is a string
  const stateIdStr = stateId.toString();
  console.log("[API] Using state ID (as string):", stateIdStr);
  
  return api.get(`/district/state/${stateIdStr}`)
    .then(response => {
      console.log("[API] Districts fetched successfully:", response.data);
      return response;
    })
    .catch(error => {
      console.error("[API] Error fetching districts:", error.message);
      console.error("[API] Error details:", error.response?.data);
      return Promise.reject(error);
    });
};
export const addDistrict = (districtData, auditContext = {}) =>
  api.post(
    '/district',
    appendAuditData(districtData, {
      action: 'create',
      resource: 'District',
      ...auditContext
    })
  );
export const updateDistrict = (districtId, districtData, auditContext = {}) =>
  api.put(
    `/district/${districtId}`,
    appendAuditData(districtData, {
      action: 'update',
      resource: 'District',
      ...auditContext
    })
  );
export const deleteDistrict = (districtId, auditContext = {}) =>
  api.delete(`/district/${districtId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'District',
      ...auditContext
    })
  });

// Masters API calls - Talukas
export const fetchTalukas = () => api.get('/taluka');
export const fetchTalukasByDistrict = (districtId) => {
  console.log("[API] Fetching talukas for district ID:", districtId);
  console.log("[API] District ID type:", typeof districtId);
  
  if (!districtId) {
    console.error("[API] District ID is missing or invalid:", districtId);
    return Promise.reject(new Error('District ID is required'));
  }
  
  // Ensure districtId is a string
  const districtIdStr = districtId.toString();
  console.log("[API] Using district ID (as string):", districtIdStr);
  
  return api.get(`/taluka/district/${districtIdStr}`)
    .then(response => {
      console.log("[API] Talukas fetched successfully:", response.data);
      return response;
    })
    .catch(error => {
      console.error("[API] Error fetching talukas:", error.message);
      console.error("[API] Error details:", error.response?.data);
      return Promise.reject(error);
    });
};
export const addTaluka = (talukaData, auditContext = {}) =>
  api.post(
    '/taluka',
    appendAuditData(talukaData, {
      action: 'create',
      resource: 'Taluka',
      ...auditContext
    })
  );
export const updateTaluka = (talukaId, talukaData, auditContext = {}) =>
  api.put(
    `/taluka/${talukaId}`,
    appendAuditData(talukaData, {
      action: 'update',
      resource: 'Taluka',
      ...auditContext
    })
  );
export const deleteTaluka = (talukaId, auditContext = {}) =>
  api.delete(`/taluka/${talukaId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'Taluka',
      ...auditContext
    })
  });

// Masters API calls - Villages
export const fetchVillages = () => api.get('/village');
export const fetchVillagesByTaluka = (talukaId) => {
  console.log("[API] Fetching villages for taluka ID:", talukaId);
  console.log("[API] Taluka ID type:", typeof talukaId);
  
  if (!talukaId) {
    console.error("[API] Taluka ID is missing or invalid:", talukaId);
    return Promise.reject(new Error('Taluka ID is required'));
  }
  
  // Ensure talukaId is a string
  const talukaIdStr = talukaId.toString();
  console.log("[API] Using taluka ID (as string):", talukaIdStr);
  
  return api.get(`/village/taluka/${talukaIdStr}`)
    .then(response => {
      console.log("[API] Villages fetched successfully:", response.data);
      return response;
    })
    .catch(error => {
      console.error("[API] Error fetching villages:", error.message);
      console.error("[API] Error details:", error.response?.data);
      return Promise.reject(error);
    });
};
export const addVillage = (villageData, auditContext = {}) =>
  api.post(
    '/village',
    appendAuditData(villageData, {
      action: 'create',
      resource: 'Village',
      ...auditContext
    })
  );
export const updateVillage = (villageId, villageData, auditContext = {}) =>
  api.put(
    `/village/${villageId}`,
    appendAuditData(villageData, {
      action: 'update',
      resource: 'Village',
      ...auditContext
    })
  );
export const deleteVillage = (villageId, auditContext = {}) =>
  api.delete(`/village/${villageId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'Village',
      ...auditContext
    })
  });

// Masters API calls - Professions
export const fetchProfessions = () => api.get('/profession');
export const addProfession = (professionData, auditContext = {}) =>
  api.post(
    '/profession',
    appendAuditData(professionData, {
      action: 'create',
      resource: 'Profession',
      ...auditContext
    })
  );
export const updateProfession = (professionId, professionData, auditContext = {}) =>
  api.put(
    `/profession/${professionId}`,
    appendAuditData(professionData, {
      action: 'update',
      resource: 'Profession',
      ...auditContext
    })
  );
export const deleteProfession = (professionId, auditContext = {}) =>
  api.delete(`/profession/${professionId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'Profession',
      ...auditContext
    })
  });

// Masters API calls - Religions
export const fetchReligions = () => api.get('/religion');
export const addReligion = (religionData, auditContext = {}) =>
  api.post(
    '/religion',
    appendAuditData(religionData, {
      action: 'create',
      resource: 'Religion',
      ...auditContext
    })
  );
export const updateReligion = (religionId, religionData, auditContext = {}) =>
  api.put(
    `/religion/${religionId}`,
    appendAuditData(religionData, {
      action: 'update',
      resource: 'Religion',
      ...auditContext
    })
  );
export const deleteReligion = (religionId, auditContext = {}) =>
  api.delete(`/religion/${religionId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'Religion',
      ...auditContext
    })
  });

// Masters API calls - Cast Categories
export const fetchCastCategories = () => api.get('/category');
export const addCastCategory = (categoryData, auditContext = {}) =>
  api.post(
    '/category',
    appendAuditData(categoryData, {
      action: 'create',
      resource: 'Cast Category',
      ...auditContext
    })
  );
export const updateCastCategory = (categoryId, categoryData, auditContext = {}) =>
  api.put(
    `/category/${categoryId}`,
    appendAuditData(categoryData, {
      action: 'update',
      resource: 'Cast Category',
      ...auditContext
    })
  );
export const deleteCastCategory = (categoryId, auditContext = {}) =>
  api.delete(`/category/${categoryId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'Cast Category',
      ...auditContext
    })
  });

// Masters API calls - Yojana Categories
export const fetchYojanaCategories = () => api.get('/yojanaCategory');
export const addYojanaCategory = (categoryData, auditContext = {}) => {
  const meta = {
    action: 'create',
    resource: 'Yojana Category',
    ...auditContext
  };

  if (!meta.message) {
    const identifier =
      auditContext?.identifier ||
      categoryData?.categoryName ||
      categoryData?.name ||
      '';
    meta.message = identifier
      ? `Created new Yojana Category : ${identifier}`
      : 'Created new Yojana Category.';
  }

  return api.post(
    '/yojanaCategory',
    appendAuditData(categoryData, meta)
  );
};
export const updateYojanaCategory = (categoryId, categoryData, auditContext = {}) =>
  api.put(
    `/yojanaCategory/${categoryId}`,
    appendAuditData(categoryData, {
      action: 'update',
      resource: 'Yojana Category',
      ...auditContext
    })
  );
export const deleteYojanaCategory = (categoryId, auditContext = {}) =>
  api.delete(`/yojanaCategory/${categoryId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'Yojana Category',
      ...auditContext
    })
  });

// File Upload API calls
export const uploadYojanaVideo = (file) => {
  const formData = new FormData();
  formData.append('file', file);

  return api.post('/s3/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

// Dynamic Forms API calls
export const fetchDynamicForms = () => api.get('/form');
export const fetchDynamicFormById = (formId) => api.get(`/form/${formId}`);
export const createDynamicForm = (formData, auditContext = {}) =>
  api.post(
    '/form',
    appendAuditData(formData, {
      action: 'create',
      resource: 'Dynamic Form',
      identifier: formData?.formTitle,
      ...auditContext
    })
  );
export const updateDynamicForm = (formId, formData, auditContext = {}) =>
  api.put(
    `/form/${formId}`,
    appendAuditData(formData, {
      action: 'update',
      resource: 'Dynamic Form',
      identifier: formData?.formTitle,
      ...auditContext
    })
  );
export const deleteDynamicForm = (formId, auditContext = {}) =>
  api.delete(`/form/${formId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'Dynamic Form',
      ...auditContext
    })
  });

// Audit Logs API
export const fetchAuditLogs = ({ pageNo = 0, pageSize = 25 } = {}) =>
  api.get('/reports/audit', {
    params: {
      pageNo,
      pageSize
    }
  });

// Masters API calls - Casts
export const fetchCasts = () => api.get('/cast');
export const fetchCastsByCategory = (categoryId) => {
    console.log("[API] Fetching casts for category ID:", categoryId);
    console.log("[API] Category ID type:", typeof categoryId);
    
    if (!categoryId) {
        console.error("[API] Category ID is missing or invalid:", categoryId);
        return Promise.reject(new Error('Category ID is required'));
    }
    
    // Ensure categoryId is a string
    const categoryIdStr = categoryId.toString();
    console.log("[API] Using category ID (as string):", categoryIdStr);
    
    return api.get(`/cast/category/${categoryIdStr}`)
        .then(response => {
            console.log("[API] Casts fetched successfully:", response.data);
            return response;
        })
        .catch(error => {
            console.error("[API] Error fetching casts:", error.message);
            console.error("[API] Error details:", error.response?.data);
            return Promise.reject(error);
        });
};
export const addCast = (castData, auditContext = {}) =>
  api.post(
    '/cast',
    appendAuditData(castData, {
      action: 'create',
      resource: 'Cast',
      ...auditContext
    })
  );
export const updateCast = (castId, castData, auditContext = {}) =>
  api.put(
    `/cast/${castId}`,
    appendAuditData(castData, {
      action: 'update',
      resource: 'Cast',
      ...auditContext
    })
  );
export const deleteCast = (castId, auditContext = {}) =>
  api.delete(`/cast/${castId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'Cast',
      ...auditContext
    })
  });

// Masters API calls - Education
export const fetchEducation = () => api.get('/education');
export const addEducation = (educationData, auditContext = {}) =>
  api.post(
    '/education',
    appendAuditData(educationData, {
      action: 'create',
      resource: 'Education',
      ...auditContext
    })
  );
export const updateEducation = (educationId, educationData, auditContext = {}) =>
  api.put(
    `/education/${educationId}`,
    appendAuditData(educationData, {
      action: 'update',
      resource: 'Education',
      ...auditContext
    })
  );
export const deleteEducation = (educationId, auditContext = {}) =>
  api.delete(`/education/${educationId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'Education',
      ...auditContext
    })
  });

// Masters API calls - Constituencies
export const fetchConstituencies = () => api.get('/constituency');
export const addConstituency = (constituencyData, auditContext = {}) =>
  api.post(
    '/constituency',
    appendAuditData(constituencyData, {
      action: 'create',
      resource: 'Constituency',
      ...auditContext
    })
  );
export const updateConstituency = (constituencyId, constituencyData, auditContext = {}) =>
  api.put(
    `/constituency/${constituencyId}`,
    appendAuditData(constituencyData, {
      action: 'update',
      resource: 'Constituency',
      ...auditContext
    })
  );
export const deleteConstituency = (constituencyId, auditContext = {}) =>
  api.delete(`/constituency/${constituencyId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'Constituency',
      ...auditContext
    })
  });

// Reports API - dynamic filter groups: POST { gender: "Male", state: "Maharashtra" }
// Response: [{ groupValues: { gender: "Male", state: "Maharashtra" }, count: 651 }, ...]
export const fetchDynamicFilterReport = (filterPayload = {}) =>
  api.post('/reports/dynamic-filter', filterPayload);

// Fetch gender report separately
export const fetchGenderReport = () => api.get('/reports/gender');

/** Parse GET /reports/gender — total is sum of all paramCount; Male/Female from paramName. */
export const parseGenderReport = (data) => {
  const items = Array.isArray(data) ? data : [];
  let male = 0;
  let female = 0;
  let total = 0;
  items.forEach((item) => {
    const count = Number(item?.paramCount) || 0;
    total += count;
    if (item?.paramName === 'Male') male = count;
    else if (item?.paramName === 'Female') female = count;
  });
  return { total, male, female };
};

export const fetchYojanaReport = () => api.get('/reports/yojana');

// Schemes API calls
export const fetchSchemes = () => api.get('/yojna');
export const fetchSchemesPage = ({ pageNo = 0, pageSize = 10 } = {}) =>
  api.get('/yojna/page', { params: { pageNo, pageSize } });
export const fetchSchemesByTitle = (title) =>
  api.get('/yojna/title', { params: { title } });
export const fetchSchemeBySerialNo = (serialNo) =>
  api.get('/yojna/serialNo', { params: { serialNo } });

export const normalizeYojanaListResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.content)) return data.content;
  if (data && typeof data === 'object' && (data.yojnaId != null || data.serialNo != null)) {
    return [data];
  }
  return [];
};
export const addScheme = (schemeData, auditContext = {}) =>
  api.post(
    '/yojna',
    appendAuditData(schemeData, {
      action: 'create',
      resource: 'Scheme',
      ...auditContext
    })
  );
export const updateScheme = (schemeId, schemeData, auditContext = {}) =>
  api.put(
    `/yojna/${schemeId}`,
    appendAuditData(schemeData, {
      action: 'update',
      resource: 'Scheme',
      ...auditContext
    })
  );
export const deleteScheme = (schemeId, auditContext = {}) =>
  api.delete(`/yojna/${schemeId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'Scheme',
      ...auditContext
    })
  });
export const publishSchemes = () => api.get('/yojna/v2/clearcache');

// Yojana approval flow (for non-admin: create/update/delete go to approval queue)
export const fetchYojanaApprovals = () => api.get('/yojana/approval');
export const fetchYojanaApprovalsPage = ({ pageNo = 0, pageSize = 10 } = {}) =>
  api.get('/yojana/approval/page', { params: { pageNo, pageSize } });
export const respondYojanaApproval = (body, auditContext = {}) => {
  const previousData = {
    id: body.id,
    yojanaId: body.yojanaId,
    status: 'PENDING',
    action: body.action,
    requestedByName: body.requestedByName
  };
  const payload = appendAuditData(body, {
    action: 'update',
    resource: 'Yojana Approval',
    identifier: body.requestedByName ? `request by ${body.requestedByName}` : `yojana ${body.yojanaId}`,
    previousData,
    newData: body,
    ...auditContext
  });
  return api.put('/yojana/approval/respond', payload);
};
export const createYojanaApproval = (data, auditContext = {}) =>
  api.post(
    '/yojana/approval',
    appendAuditData(data, {
      action: 'create',
      resource: 'Scheme',
      identifier: data?.title || data?.yojnaId,
      ...auditContext
    })
  );
export const updateYojanaApproval = (yojanaId, data, auditContext = {}) =>
  api.put(
    `/yojana/approval/${yojanaId}`,
    appendAuditData(data, {
      action: 'update',
      resource: 'Scheme',
      identifier: data?.title || auditContext.previousData?.title || data?.yojnaId || yojanaId,
      previousData: auditContext.previousData,
      newData: data,
      ...auditContext
    })
  );
export const deleteYojanaApproval = (yojanaId, data, auditContext = {}) =>
  api.delete(`/yojana/approval/${yojanaId}`, {
    data: appendAuditData(data || {}, {
      action: 'delete',
      resource: 'Scheme',
      identifier: auditContext.identifier ?? data?.title ?? yojanaId,
      previousData: data,
      ...auditContext
    })
  });

// User Groups API calls
export const fetchUserGroups = async () => {
    try {
        const response = await api.get('/usergroup');
        console.log('API Response for user groups:', response);
        return response;
    } catch (error) {
        console.error('API Error fetching user groups:', error);
        throw error;
    }
};
export const addUserGroup = (groupData, auditContext = {}) =>
  api.post(
    '/usergroup',
    appendAuditData(groupData, {
      action: 'create',
      resource: 'User Group',
      ...auditContext
    })
  );
export const updateUserGroup = (userGroupId, groupData, auditContext = {}) =>
  api.post(
    '/usergroup',
    appendAuditData(
      {
        ...groupData,
        userGroupId
      },
      {
        action: 'update',
        resource: 'User Group',
        ...auditContext
      }
    )
  );
export const deleteUserGroup = (groupId, auditContext = {}) =>
  api.delete(`/usergroup/${groupId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'User Group',
      ...auditContext
    })
  });

// System Users API calls
export const fetchSystemUsers = () => api.get('/adminuser/admin');
export const addSystemUser = (userData, auditContext = {}) =>
  api.post(
    '/adminuser',
    appendAuditData(userData, {
      action: 'create',
      resource: 'System User',
      ...auditContext
    })
  );
export const updateSystemUser = (userId, userData, auditContext = {}) =>
  api.put(
    `/adminuser/${userId}`,
    appendAuditData(userData, {
      action: 'update',
      resource: 'System User',
      ...auditContext
    })
  );
export const deleteSystemUser = (userId, auditContext = {}) =>
  api.delete(`/adminuser/${userId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'System User',
      ...auditContext
    })
  });

// App Users API calls
export const fetchAppUsers = () => api.get('/v2/registration');
export const fetchAppUsersPage = ({ pageNo = 0, pageSize = 10 } = {}) =>
  api.get('/v2/registration/page', { params: { pageNo, pageSize } });
export const fetchAppUserById = (userId) => api.get(`/app-users/${userId}`);
export const fetchAppUsersByFilter = (filters) => {
  const params = new URLSearchParams();
  if (filters.firstName) params.append('firstName', filters.firstName);
  if (filters.lastName) params.append('lastName', filters.lastName);
  if (filters.mobile) params.append('mobile', filters.mobile);
  return api.get(`/user/app/filter?${params.toString()}`);
};
export const addAppUser = (userData, auditContext = {}) =>
  api.post(
    '/registration',
    appendAuditData(userData, {
      action: 'create',
      resource: 'App User',
      ...auditContext
    })
  );
export const updateAppUser = (userId, userData, auditContext = {}) =>
  api.put(
    `/registration/${userId}`,
    appendAuditData(userData, {
      action: 'update',
      resource: 'App User',
      ...auditContext
    })
  );
export const deleteAppUser = (userId, auditContext = {}) =>
  api.delete(`/registration/${userId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'App User',
      ...auditContext
    })
  });

// Auth API calls
export const login = (credentials, auditContext = {}) =>
  api.post(
    '/validate/admin/login',
    appendAuditData(credentials, {
      action: 'login',
      resource: 'User',
      message: 'User logged in to the panel.',
      ...auditContext
    })
  );

// Ads Module - Ad User Auth
export const adsUserSignup = (userData) =>
  api.post('/add/user/signup', userData);

export const adsUserLogin = (credentials) =>
  api.post('/add/user/login', credentials);

export const validateAdsUsername = (username) =>
  api.get(`/add/user/username/${encodeURIComponent(username)}`);

// Ads module — Ads Category master (Masters tab). Base path: /add/category
export const fetchAdCategories = () => api.get('/add/category');

export const addAdCategory = (categoryData, auditContext = {}) =>
  api.post(
    '/add/category',
    appendAuditData(categoryData, {
      action: 'create',
      resource: 'Ads Category',
      ...auditContext
    })
  );

export const updateAdCategory = (categoryId, categoryData, auditContext = {}) =>
  api.put(
    `/add/category/${categoryId}`,
    appendAuditData(categoryData, {
      action: 'update',
      resource: 'Ads Category',
      ...auditContext
    })
  );

export const deleteAdCategory = (categoryId, auditContext = {}) =>
  api.delete(`/add/category/${categoryId}`, {
    data: buildAuditOnlyPayload({
      action: 'delete',
      resource: 'Ads Category',
      ...auditContext
    })
  });

// Ads Module - Ads CRUD & Admin tools
export const createAd = (adData) => api.post('/add', adData);

// For ad creators: list their ads. Backend is expected to filter by token.
export const fetchAds = () => api.get('/add');

// For admin panel: get ads by status (0 pending, 1 approved, 2 expired, 3 rejected)
export const fetchAdsByStatus = (status) => api.get(`/add/status/${status}`);

/** PUT /add/:id/status — body: { status: 0 | 1 | 2 | 3 } */
export const updateAdStatus = (adId, status) =>
  api.put(`/add/${adId}/status`, { status });

/** GET /add/event/analytics/:parentAdId */
export const fetchAdEventAnalytics = (parentAdId) =>
  api.get(`/add/event/analytics/${encodeURIComponent(parentAdId)}`);

export default api; 